import { ArrowClockwiseIcon, CaretRightIcon, CheckIcon, CopySimpleIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { GhostButton } from '../../components/buttons/ghost-button.tsx'
import { Spinner } from '../../components/spinner.tsx'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import {
  invalidateGetRepositoryOpenGrepRules,
  useGetRepositoryOpenGrepRules,
  useGetRepositoryOpenGrepRulesContent,
  useGetRepositoryThreatModel,
  useOpenGrepRulesStream,
  usePostRepositoryOpenGrepRules,
  type OpenGrepRule,
} from '../../utils/api-client/api-hooks.ts'
import { OpenGrepRulesPhaseSchema, isInFlightGenerationStatus } from '../../utils/api-client/api-schemas.ts'
import { formatDate, formatRelativeTime } from '../../utils/date-utils.ts'
import { joinMetaSegments, pluralize } from '../../utils/text-utils.ts'
import * as styles from './opengrep-rules-tab.css.ts'
import { PhaseStepper } from './phase-stepper.tsx'
import { useRepositoryDetailsPageParams } from './repository-details-page.tsx'

type SupportedOpenGrepRulesPhase = Exclude<
  import('../../utils/api-client/api-schemas.ts').OpenGrepRulesPhase,
  'unknown'
>

const OPENGREP_RULES_PHASES = OpenGrepRulesPhaseSchema.options.filter(
  (phase): phase is SupportedOpenGrepRulesPhase => phase !== 'unknown'
)

const PHASE_LABELS: Record<SupportedOpenGrepRulesPhase, string> = {
  queued: 'Queued',
  generating: 'Generating rules',
}

// The contract leaves `phase` a bare string, so the response can name a phase the stepper has never
// heard of, and the stream can report `unknown` when the live phase is indeterminate. Both mean the
// same thing here — a run is moving but we cannot say where — so both render as the first phase
// rather than mis-indexing the stepper.
const toSupportedPhase = (phase: string): SupportedOpenGrepRulesPhase => {
  const knownPhase = OpenGrepRulesPhaseSchema.catch('unknown').parse(phase)
  return knownPhase === 'unknown' ? 'queued' : knownPhase
}

const NOISE_LEVEL_LABEL: Record<'high' | 'medium' | 'low', string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

const TabHeader: React.FC = () => (
  <div className={styles.header}>
    <h2 className={styles.headerTitle}>Pixee Generated Rules</h2>
    <p className={styles.headerDescription}>
      Pixee uses our context graph, comprised of threat modeling outputs, organization preferences, user feedback, and
      more, to generate opengrep rules tailored to your repository.
    </p>
  </div>
)

export type OpenGrepRulesTabProps = {
  onSwitchToThreatModel: () => unknown
}

export const OpenGrepRulesTab: React.FC<OpenGrepRulesTabProps> = ({ onSwitchToThreatModel }) => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const queryClient = useQueryClient()

  const { data: threatModel, isLoading: isThreatModelLoading } = useGetRepositoryThreatModel({ repositoryId })
  const { data: openGrepRules, isLoading: isRulesLoading } = useGetRepositoryOpenGrepRules({ repositoryId })

  const isInFlight = !!openGrepRules && isInFlightGenerationStatus(openGrepRules.status)
  const stream = useOpenGrepRulesStream(isInFlight ? repositoryId : undefined)
  const lastStreamEvent = stream.events.at(-1)
  const firstStreamEvent = stream.events.at(0)

  useEffect(() => {
    if (lastStreamEvent && lastStreamEvent.status !== 'processing') {
      invalidateGetRepositoryOpenGrepRules(queryClient, { repositoryId })
    }
  }, [lastStreamEvent, queryClient, repositoryId])

  const hasContentLink = !!openGrepRules?._links.content
  const { data: content, isLoading: isContentLoading } = useGetRepositoryOpenGrepRulesContent({
    repositoryId,
    generatedAt: openGrepRules?.generatedAt,
    enabled: hasContentLink,
  })

  const showGenerateError = useShowMutationError<typeof usePostRepositoryOpenGrepRules>({
    prefix: 'Failed to start opengrep rule generation',
  })
  const generateMutation = usePostRepositoryOpenGrepRules({
    onSuccess: () => invalidateGetRepositoryOpenGrepRules(queryClient, { repositoryId }),
    onError: showGenerateError,
  })
  const isStarting = generateMutation.isPending
  const onGenerate = useCallback(
    () => generateMutation.mutate({ repositoryId }),
    [generateMutation.mutate, repositoryId]
  )

  const isThreatModelReady = !!threatModel?._links.content

  // Stale: rules were generated before the latest threat model
  const isStale =
    !!openGrepRules?.generatedAt &&
    !!threatModel?.generatedAt &&
    new Date(threatModel.generatedAt) > new Date(openGrepRules.generatedAt)

  if (isRulesLoading || isThreatModelLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading opengrep rules" />
      </div>
    )
  }

  if (!openGrepRules) {
    return (
      <div className={styles.container}>
        <TabHeader />
        {isThreatModelReady ? (
          <OpenGrepRulesEmptyState onGenerate={onGenerate} isStarting={isStarting} />
        ) : (
          <OpenGrepRulesPrerequisiteChecklist onGenerateThreatModel={onSwitchToThreatModel} />
        )}
      </div>
    )
  }

  const currentPhase = toSupportedPhase(lastStreamEvent?.phase ?? openGrepRules.phase)
  const startedAt = firstStreamEvent?.timestamp

  return (
    <div className={styles.container}>
      <TabHeader />
      {match(openGrepRules.status)
        .with('processing', 'unknown', () =>
          content ? (
            <>
              <RegeneratingBanner phase={currentPhase} requestedBy={openGrepRules.requestedBy} startedAt={startedAt} />
              <RulesContent rules={content.rules} isDimmed />
            </>
          ) : (
            <GeneratingState phase={currentPhase} requestedBy={openGrepRules.requestedBy} startedAt={startedAt} />
          )
        )
        .with('failed', () => (
          <>
            <FailedBanner
              failureReason={openGrepRules.failureReason}
              onRetry={onGenerate}
              isRetrying={isStarting}
              canRetry={isThreatModelReady}
            />
            {content && <RulesContent rules={content.rules} />}
          </>
        ))
        .with('completed', () => (
          <>
            <MetaRow
              generatedAt={openGrepRules.generatedAt}
              requestedBy={openGrepRules.requestedBy}
              ruleCount={content?.rules.length}
              isStale={isStale}
              onRegenerate={onGenerate}
              isStarting={isStarting}
              canRegenerate={isThreatModelReady}
            />
            {match({ content, isContentLoading })
              .with({ content: { rules: [] } }, () => <NoRulesState />)
              .when(
                ({ content: c }) => !!c,
                ({ content: c }) => <RulesContent rules={c!.rules} />
              )
              .with({ isContentLoading: true }, () => (
                <div className={styles.contentLoadingState}>
                  <Spinner label="Loading rules" />
                </div>
              ))
              .otherwise(() => (
                <UnavailableContent />
              ))}
          </>
        ))
        .exhaustive()}
    </div>
  )
}

// #region Empty / prerequisite states

const OpenGrepRulesPrerequisiteChecklist: React.FC<{ onGenerateThreatModel: () => unknown }> = ({
  onGenerateThreatModel,
}) => (
  <div className={styles.prerequisite}>
    <div>
      <p className={styles.prerequisiteTitle}>Opengrep rules need a little setup</p>
      <p className={styles.prerequisiteDescription}>
        Rules are generated from this repository's threat model. Complete the prerequisite below to enable generation.
      </p>
    </div>
    <ul className={styles.prerequisiteList}>
      <li className={styles.prerequisiteItem}>
        <span className={styles.prerequisiteMarker} />
        <div>
          <p className={styles.prerequisiteItemTitle}>Threat model generated</p>
          <button className={styles.prerequisiteAction} onClick={onGenerateThreatModel}>
            Generate a threat model
          </button>
        </div>
      </li>
    </ul>
  </div>
)

const OpenGrepRulesEmptyState: React.FC<{ onGenerate: () => unknown; isStarting: boolean }> = ({
  onGenerate,
  isStarting,
}) => (
  <div className={styles.emptyState}>
    <p className={styles.emptyStateTitle}>No opengrep rules yet</p>
    <p className={styles.emptyStateDescription}>
      Generate repository-specific opengrep rules targeting authentication, authorization, and business-logic patterns.
      Generation usually takes under a minute.
    </p>
    <DefaultButton buttonType="primary" state={isStarting ? 'loading' : 'default'} onClick={onGenerate}>
      Generate rules
    </DefaultButton>
  </div>
)

const NoRulesState: React.FC = () => (
  <div className={styles.placeholderState}>
    <p>No rules were generated for this repository.</p>
  </div>
)

const UnavailableContent: React.FC = () => (
  <div className={styles.placeholderState}>
    <p>Rule content is unavailable. Regenerating usually resolves it.</p>
  </div>
)

// #endregion

// #region Progress states

type GeneratingStateProps = {
  phase: SupportedOpenGrepRulesPhase
  requestedBy?: string
  startedAt?: string
}

const GeneratingState: React.FC<GeneratingStateProps> = ({ phase, requestedBy, startedAt }) => (
  <div className={styles.generatingState}>
    <PhaseStepper
      phases={OPENGREP_RULES_PHASES}
      labels={PHASE_LABELS}
      currentPhase={phase}
      currentColor={mappedColors.primary.pulse}
    />
    <div className={styles.generatingStatus}>
      <p className={styles.generatingLabel}>
        <Spinner size="16" label="Generating rules" />
        Generating rules
      </p>
      <p className={styles.generatingHint}>
        {joinMetaSegments([
          requestedBy && `Requested by ${requestedBy}`,
          startedAt && `Started ${formatRelativeTime(startedAt)}`,
        ])}
      </p>
      <p className={styles.generatingHint}>
        Usually takes under a minute. You can leave this page — generation continues in the background.
      </p>
    </div>
  </div>
)

const RegeneratingBanner: React.FC<GeneratingStateProps> = ({ phase, requestedBy, startedAt }) => (
  <div role="status" aria-label="Regenerating rules" className={styles.regeneratingBanner}>
    <Spinner size="16" />
    {joinMetaSegments([
      `Regenerating — ${PHASE_LABELS[phase]} (${OPENGREP_RULES_PHASES.indexOf(phase) + 1}/${OPENGREP_RULES_PHASES.length})`,
      requestedBy && `requested by ${requestedBy}`,
      startedAt && `started ${formatRelativeTime(startedAt)}`,
    ])}
  </div>
)

// #endregion

// #region Failed / meta

const FailedBanner: React.FC<{
  failureReason?: string
  onRetry: () => unknown
  isRetrying: boolean
  canRetry: boolean
}> = ({ failureReason, onRetry, isRetrying, canRetry }) => (
  <div role="alert" className={styles.failedBanner}>
    <p className={styles.failedBannerTitle}>
      <WarningCircleIcon size={20} weight="fill" />
      Rule generation failed
    </p>
    <p className={styles.failedBannerReason}>{failureReason ?? 'Something went wrong while generating the rules.'}</p>
    <GhostButton
      size="small"
      state={isRetrying || !canRetry ? 'disabled' : 'default'}
      icon={<ArrowClockwiseIcon size={14} weight="light" />}
      onClick={onRetry}
    >
      Try again
    </GhostButton>
  </div>
)

const MetaRow: React.FC<{
  generatedAt?: string | null
  requestedBy?: string
  ruleCount?: number
  isStale: boolean
  onRegenerate: () => unknown
  isStarting: boolean
  canRegenerate: boolean
}> = ({ generatedAt, requestedBy, ruleCount, isStale, onRegenerate, isStarting, canRegenerate }) => (
  <div className={styles.metaRow}>
    <p className={styles.metaSummary}>
      {joinMetaSegments([
        generatedAt && `Generated ${formatDate({ date: generatedAt, type: 'mm/dd/yyyy-hh:mm' })}`,
        ruleCount !== undefined ? `${ruleCount} ${pluralize(ruleCount, 'rule', 'rules')}` : undefined,
        requestedBy && `requested by ${requestedBy}`,
      ])}
      {isStale && <span className={styles.staleBadge}>Stale — threat model updated</span>}
    </p>
    <DefaultButton
      buttonType="secondary"
      size="small"
      state={isStarting ? 'loading' : canRegenerate ? 'default' : 'disabled'}
      onClick={onRegenerate}
    >
      Regenerate
    </DefaultButton>
  </div>
)

// #endregion

// #region Rules table

const RulesContent: React.FC<{ rules: OpenGrepRule[]; isDimmed?: boolean }> = ({ rules, isDimmed = false }) => (
  <div className={classNames(styles.rulesContent, isDimmed && styles.rulesContentDimmed)}>
    <table className={styles.table}>
      <colgroup>
        <col className={styles.colRuleId} />
        <col />
        <col className={styles.colNoise} />
        <col className={styles.colCaret} />
      </colgroup>
      <thead>
        <tr className={styles.headerRow}>
          <th className={classNames(styles.headerCell, styles.headerCellFirst)}>Rule ID</th>
          <th className={styles.headerCell}>Name</th>
          <th className={styles.headerCell}>Noise</th>
          <th className={classNames(styles.headerCell, styles.headerCellLast)} />
        </tr>
      </thead>
      <tbody>
        {rules.map((rule, i) => (
          <RuleRow key={rule.rule_id} rule={rule} index={i} />
        ))}
      </tbody>
    </table>
  </div>
)

const RuleRow: React.FC<{ rule: OpenGrepRule; index: number }> = ({ rule, index }) => {
  const [isOpen, setIsOpen] = useState(false)
  const toggle = () => setIsOpen(open => !open)

  return (
    <>
      {/* A row, not a button, so the table semantics survive — but it is focusable and answers
          Enter/Space, so the drawer is reachable without a mouse. Hover and expanded borders are
          CSS keyed off `aria-expanded`. */}
      <tr
        aria-expanded={isOpen}
        tabIndex={0}
        onClick={toggle}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            toggle()
          }
        }}
        className={classNames(styles.ruleRow, index % 2 === 0 ? styles.ruleRowEven : styles.ruleRowOdd)}
      >
        <td className={styles.ruleIdCell}>{rule.rule_id}</td>
        <td className={styles.ruleNameCell}>
          <span className={styles.ruleName}>{rule.name}</span>
          {rule.rationale && <p className={styles.ruleRationale}>{rule.rationale}</p>}
        </td>
        <td className={styles.ruleNoiseCell}>
          <NoiseBadge level={rule.noise_level} />
        </td>
        <td className={styles.ruleCaretCell}>
          <CaretRightIcon
            size={14}
            color={isOpen ? mappedColors.neutral.neutral30 : mappedColors.neutral.halo}
            className={styles.ruleCaret}
          />
        </td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={4} className={styles.drawerCell}>
            <RuleDrawer rule={rule} />
          </td>
        </tr>
      )}
    </>
  )
}

const NoiseBadge: React.FC<{ level: 'high' | 'medium' | 'low' }> = ({ level }) => (
  <span className={styles.noiseBadge[level]}>{NOISE_LEVEL_LABEL[level]}</span>
)

const BRACE_LANGS = new Set(['javascript', 'typescript', 'js', 'ts', 'jsx', 'tsx', 'java', 'go', 'c', 'cpp'])

/**
 * Lightweight single-line → multi-line formatter for brace-based languages, used because the rule
 * examples often arrive as one long line. Only runs when the code has no existing newlines.
 *
 * Strings and comments are copied through verbatim so a `{`, `}` or `;` inside them is not mistaken
 * for structure — that would reflow the snippet wrongly, and comments are common in these examples.
 * Regex literals are the known gap: `/` is ambiguous without parsing, so a regex containing braces
 * or semicolons can still reflow. Best-effort presentation, not a parser; the snippet stays readable
 * either way because no characters are dropped.
 */
function formatCode(code: string, language: string): string {
  if (code.includes('\n')) {
    return code
  }
  if (!BRACE_LANGS.has(language.toLowerCase())) {
    return code
  }

  let out = ''
  let depth = 0
  let inStr = false
  let strChar = ''

  for (let i = 0; i < code.length; i++) {
    const ch = code[i] ?? ''
    const next = code[i + 1] ?? ''

    if (inStr) {
      out += ch
      if (ch === '\\') {
        out += next
        i++
      } else if (ch === strChar) {
        inStr = false
      }
      continue
    }

    if (ch === '"' || ch === "'" || ch === '`') {
      inStr = true
      strChar = ch
      out += ch
      continue
    }

    // A `//` comment runs to the end of the line, and this only ever formats single-line input, so
    // everything after it is comment — emit the rest untouched.
    if (ch === '/' && next === '/') {
      out += code.slice(i)
      break
    }

    // A `/* … */` comment: copy it whole, braces and semicolons included.
    if (ch === '/' && next === '*') {
      const close = code.indexOf('*/', i + 2)
      const commentEnd = close === -1 ? code.length : close + 2
      out += code.slice(i, commentEnd)
      i = commentEnd - 1
      continue
    }

    if (ch === '{') {
      depth++
      out += ' {\n' + '  '.repeat(depth)
    } else if (ch === '}') {
      depth = Math.max(0, depth - 1)
      out = out.trimEnd()
      out += '\n' + '  '.repeat(depth) + '}'
      if (next === ';') {
        out += ';\n' + '  '.repeat(depth)
        i++
      } else if (next === ',') {
        out += ',\n' + '  '.repeat(depth)
        i++
      }
    } else if (ch === ';') {
      out += ';\n' + '  '.repeat(depth)
    } else {
      out += ch
    }
  }

  return out.trim()
}

const RuleCodeBlock: React.FC<{ code: string; language: string; variant: 'yaml' | 'good' | 'bad' }> = ({
  code,
  language,
  variant,
}) => (
  <pre
    className={classNames(
      styles.codeBlock,
      variant === 'yaml' && styles.codeBlockYaml,
      variant === 'good' && classNames(styles.codeBlockGood, styles.codeBlockFlex),
      variant === 'bad' && classNames(styles.codeBlockBad, styles.codeBlockFlex)
    )}
  >
    {formatCode(code, language).trim()}
  </pre>
)

const RuleDrawer: React.FC<{ rule: OpenGrepRule }> = ({ rule }) => {
  const [copied, setCopied] = useState(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(rule.opengrep_yaml).then(() => {
      setCopied(true)
      if (resetTimer.current) clearTimeout(resetTimer.current)
      resetTimer.current = setTimeout(() => setCopied(false), 2000)
    })
  }, [rule.opengrep_yaml])

  return (
    <div className={styles.drawer}>
      <div className={styles.drawerYamlSection}>
        <p className={styles.drawerLabel}>Opengrep Rule YAML</p>
        <RuleCodeBlock code={rule.opengrep_yaml} language="yaml" variant="yaml" />
        <button
          onClick={handleCopy}
          className={classNames(styles.copyButton, copied && styles.copyButtonCopied)}
          aria-label="Copy rule YAML"
        >
          {copied ? <CheckIcon size={18} weight="bold" /> : <CopySimpleIcon size={18} />}
        </button>
      </div>
      <div className={styles.drawerExamples}>
        <div className={styles.drawerExample}>
          <p className={classNames(styles.drawerLabel, styles.drawerLabelGood)}>Good example (safe)</p>
          <RuleCodeBlock code={rule.good_code_example} language={rule.language} variant="good" />
        </div>
        <div className={styles.drawerExample}>
          <p className={classNames(styles.drawerLabel, styles.drawerLabelBad)}>Bad example (flagged)</p>
          <RuleCodeBlock code={rule.bad_code_example} language={rule.language} variant="bad" />
        </div>
      </div>
    </div>
  )
}

// #endregion
